import { Integration } from "./integrations";
import { SchemaType } from "./schemas/schemas";
import { BaseTrigger } from "./triggers";

export enum YotpoIntegrationTriggerKey {
  YOTPO_LOYALTY_EXPIRATION_REMINDER = "yotpo_loyalty_expiration_reminder",
  YOTPO_LOYALTY_POINTS_REMINDER = "yotpo_loyalty_points_reminder",
  YOTPO_LOYALTY_REDEMPTION_REMINDER = "yotpo_loyalty_redemption_reminder",
  YOTPO_LOYALTY_REDEMPTION_CREATED = "yotpo_loyalty_redemption_created",
  YOTPO_LOYALTY_REFERRAL_COMPLETED = "yotpo_loyalty_referral_completed",
  YOTPO_LOYALTY_REFERRAL_SHARED = "yotpo_loyalty_referral_shared",
  YOTPO_LOYALTY_CUSTOMER_BIRTHDAY = "yotpo_loyalty_customer_birthday",
  YOTPO_LOYALTY_OPT_IN = "yotpo_loyalty_opt_in",
  YOTPO_LOYALTY_TIER_EARNED = "yotpo_loyalty_tier_earned",
  YOTPO_LOYALTY_TIER_LOST = "yotpo_loyalty_tier_lost",
  YOTPO_LOYALTY_POINTS_EARNED = "yotpo_loyalty_points_earned",
}
interface BaseIntegrationTrigger extends BaseTrigger {
  category: "Integration";
  integration: Integration;
}

export type YotpoIntegrationLoyaltyExpirationReminderTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_EXPIRATION_REMINDER;
  };

export type YotpoIntegrationLoyaltyPointsReminderTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_POINTS_REMINDER;
  };

export type YotpoIntegrationLoyaltyRedemptionReminderTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REDEMPTION_REMINDER;
  };

export type YotpoIntegrationLoyaltyRedemptionCreatedTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REDEMPTION_CREATED;
  };

export type YotpoIntegrationLoyaltyReferralCompletedTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REFERRAL_COMPLETED;
  };

export type YotpoIntegrationLoyaltyReferralSharedTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REFERRAL_SHARED;
  };

export type YotpoIntegrationLoyaltyCustomerBirthdayTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY;
  };

export type YotpoIntegrationLoyaltyOptInTrigger = BaseIntegrationTrigger & {
  key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_OPT_IN;
};

export type YotpoIntegrationLoyaltyTierEarnedTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_TIER_EARNED;
  };

export type YotpoIntegrationLoyaltyTierLostTrigger = BaseIntegrationTrigger & {
  key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_TIER_LOST;
};

export type YotpoIntegrationLoyaltyPointsEarnedTrigger =
  BaseIntegrationTrigger & {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_POINTS_EARNED;
  };

export type YotpoIntegrationTrigger =
  | YotpoIntegrationLoyaltyExpirationReminderTrigger
  | YotpoIntegrationLoyaltyPointsReminderTrigger
  | YotpoIntegrationLoyaltyRedemptionReminderTrigger
  | YotpoIntegrationLoyaltyRedemptionCreatedTrigger
  | YotpoIntegrationLoyaltyReferralCompletedTrigger
  | YotpoIntegrationLoyaltyReferralSharedTrigger
  | YotpoIntegrationLoyaltyCustomerBirthdayTrigger
  | YotpoIntegrationLoyaltyOptInTrigger
  | YotpoIntegrationLoyaltyTierEarnedTrigger
  | YotpoIntegrationLoyaltyTierLostTrigger
  | YotpoIntegrationLoyaltyPointsEarnedTrigger;

export const yotpoIntegrationTriggers: YotpoIntegrationTrigger[] = [
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_EXPIRATION_REMINDER,
    displayName: "Yotpo loyalty expiration reminder",
    category: "Integration",
    description: "Fires when a customer's loyalty points are about to expire",
    schemaType: SchemaType.YOTPO_LOYALTY_EXPIRATION_REMINDER,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_POINTS_REMINDER,
    displayName: "Yotpo loyalty points reminder",
    category: "Integration",
    description: "Fires when a customer needs more points for a reward",
    schemaType: SchemaType.YOTPO_LOYALTY_POINTS_REMINDER,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REDEMPTION_REMINDER,
    displayName: "Yotpo loyalty redemption reminder",
    category: "Integration",
    description: "Fires to remind a customer about available redemptions",
    schemaType: SchemaType.YOTPO_LOYALTY_REDEMPTION_REMINDER,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REDEMPTION_CREATED,
    displayName: "Yotpo loyalty redemption created",
    category: "Integration",
    description: "Fires when a customer creates a new redemption",
    schemaType: SchemaType.YOTPO_LOYALTY_REDEMPTION_CREATED,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REFERRAL_COMPLETED,
    displayName: "Yotpo loyalty referral completed",
    category: "Integration",
    description: "Fires when a referral is completed and rewards are issued",
    schemaType: SchemaType.YOTPO_LOYALTY_REFERRAL_COMPLETED,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_REFERRAL_SHARED,
    displayName: "Yotpo loyalty referral shared",
    category: "Integration",
    description: "Fires when a customer shares their referral link",
    schemaType: SchemaType.YOTPO_LOYALTY_REFERRAL_SHARED,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY,
    displayName: "Yotpo loyalty customer birthday",
    category: "Integration",
    description: "Fires on a customer's birthday with loyalty rewards",
    schemaType: SchemaType.YOTPO_LOYALTY_CUSTOMER_BIRTHDAY,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_OPT_IN,
    displayName: "Yotpo loyalty opt in",
    category: "Integration",
    description: "Fires when a customer opts into the loyalty program",
    schemaType: SchemaType.YOTPO_LOYALTY_OPT_IN,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_TIER_EARNED,
    displayName: "Yotpo loyalty tier earned",
    category: "Integration",
    description: "Fires when a customer earns a new loyalty tier",
    schemaType: SchemaType.YOTPO_LOYALTY_TIER_EARNED,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_TIER_LOST,
    displayName: "Yotpo loyalty tier lost",
    category: "Integration",
    description: "Fires when a customer loses a loyalty tier",
    schemaType: SchemaType.YOTPO_LOYALTY_TIER_LOST,
    integration: Integration.YOTPO,
  },
  {
    key: YotpoIntegrationTriggerKey.YOTPO_LOYALTY_POINTS_EARNED,
    displayName: "Yotpo loyalty points earned",
    category: "Integration",
    description: "Fires when a customer earns loyalty points",
    schemaType: SchemaType.YOTPO_LOYALTY_POINTS_EARNED,
    integration: Integration.YOTPO,
  },
];

// Recharge Integration Triggers
export enum RechargeIntegrationTriggerKey {
  UPCOMING_CHARGE = "upcoming_product_subscription_charge",
}

interface BaseRechargeIntegrationTrigger extends BaseTrigger {
  category: "Integration";
  integration: Integration.RECHARGE;
}

export type RechargeUpcomingChargeTrigger = BaseRechargeIntegrationTrigger & {
  key: RechargeIntegrationTriggerKey.UPCOMING_CHARGE;
};

export type RechargeIntegrationTrigger = RechargeUpcomingChargeTrigger;

export const rechargeIntegrationTriggers: RechargeIntegrationTrigger[] = [
  {
    key: RechargeIntegrationTriggerKey.UPCOMING_CHARGE,
    displayName: "Upcoming product subscription charge",
    category: "Integration",
    description: "Fires before recurring subscription charge",
    schemaType: SchemaType.PRODUCT_SUBSCRIPTION_UPCOMING_CHARGE,
    integration: Integration.RECHARGE,
  },
];

// LoyaltyLion Integration Triggers
export enum LoyaltyLionIntegrationTriggerKey {
  LOYALTY_LION_ENROLLED = "loyalty_lion_enrolled",
  LOYALTY_LION_REWARD_AVAILABLE = "loyalty_lion_reward_available",
}

interface BaseLoyaltyLionIntegrationTrigger extends BaseTrigger {
  category: "Integration";
  integration: Integration.LOYALTY_LION;
}

export type LoyaltyLionEnrolledTrigger = BaseLoyaltyLionIntegrationTrigger & {
  key: LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_ENROLLED;
};

export type LoyaltyLionRewardAvailableTrigger =
  BaseLoyaltyLionIntegrationTrigger & {
    key: LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_REWARD_AVAILABLE;
  };

export type LoyaltyLionIntegrationTrigger =
  | LoyaltyLionEnrolledTrigger
  | LoyaltyLionRewardAvailableTrigger;

export const loyaltyLionIntegrationTriggers: LoyaltyLionIntegrationTrigger[] = [
  {
    key: LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_REWARD_AVAILABLE,
    displayName: "LoyaltyLion reward available",
    category: "Integration",
    description:
      "Fires when a customer becomes eligible to claim a reward in their LoyaltyLion account",
    schemaType: SchemaType.LOYALTY_LION_REWARD_AVAILABLE,
    integration: Integration.LOYALTY_LION,
  },
  {
    key: LoyaltyLionIntegrationTriggerKey.LOYALTY_LION_ENROLLED,
    displayName: "LoyaltyLion enrollment",
    category: "Integration",
    description: "Customer enrolls in the LoyaltyLion loyalty program",
    schemaType: SchemaType.LOYALTY_LION_ENROLLED,
    integration: Integration.LOYALTY_LION,
  },
];
